import { DELAYED_SHIPPING_EXPIRATION, getDefaultExpirationDateInUTC, SEND_ON_HOLD } from '../constants/bucket';
import { DateFormatsEnum } from '../constants/date';
import { CRM_TYPE, DISABLE_EMAIL_NOTIFICATIONS, DISABLE_SENDER_EMAIL_NOTIFICATIONS } from '../constants/shipping';
import {
  IFlowStepProps,
  IOneLinkEngagementCandidate,
  TEngagementCandidate,
  TEngagementRequestPayload,
} from '../types/bucket';
import { ICampaignItem } from '../types/campaigns';
import { IMappedMskuItem, IOnDemandItem, IRequestOnDemandItem } from '../types/inventories';
import { formatDate } from './date';
import { isItemMsku, isItemOnDemand, mapMskuItemToRequest } from './inventories';
import { buildMarketplaceRequestMap, splitBucketBySource } from './marketplacePayload';
import { isAwaitingAddress } from './reports';

type EngagementMapperMetadata = Omit<IFlowStepProps, 'className' | 'SenderInfo' | 'isPYG'> & {
  crmType: 'salesforce' | 'hubspot' | 'none';
};

export type TEngagementRequestItem = ICampaignItem | IRequestOnDemandItem | IMappedMskuItem;

export const mapItemsToRequest = (items: (ICampaignItem | IOnDemandItem)[]): TEngagementRequestItem[] =>
  items.map((item) => {
    if (isItemOnDemand(item as IOnDemandItem)) {
      const { item_id, ...onDemandRequestItem } = item as IOnDemandItem;
      return onDemandRequestItem;
    }
    if (isItemMsku(item) && (item as IOnDemandItem | ICampaignItem).sku_options?.length) {
      return mapMskuItemToRequest(item as IOnDemandItem | ICampaignItem);
    }

    return item;
  });

/**
 * Maps the engagement candidate to the engagement request payload.
 *
 * @param {TEngagementCandidate | IOneLinkEngagementCandidate} engagement - The engagement candidate to be mapped.
 * @param {EngagementMapperMetadata} metadata - Additional metadata for the engagement mapping.
 * @returns {TEngagementRequestPayload} - The mapped engagement request payload.
 */
export const mapEngagementCandidateToRequest = (
  engagement: TEngagementCandidate | IOneLinkEngagementCandidate,
  metadata: EngagementMapperMetadata,
): TEngagementRequestPayload => {
  const {
    items: engagementItems,
    pick_campaigns,
    disable_sender_email_notifications,
    marketplace_items: marketplaceItems,
    marketplace_pyg_ids: marketplacePygIds,
    gift_budget: giftBudget,
    ...restEngagement
  } = engagement;
  const { isOneLink, crmType } = metadata;
  const { on_hold_until, delayed_shipping_expiration } = engagement;

  const isDelayedShipping = !isOneLink && isAwaitingAddress(engagement as TEngagementCandidate);

  // Marketplace picks travel in the `marketplace` map, never in `items[]`.
  const ieItems = engagementItems && splitBucketBySource(engagementItems).ieItems;

  const items = ieItems ? mapItemsToRequest(ieItems) : undefined;

  const onHoldUntil = (() => {
    if (on_hold_until?.length) {
      return on_hold_until;
    }
    return undefined;
  })();

  const expirationDate = (() => {
    if (!isDelayedShipping) {
      return;
    }
    if (delayed_shipping_expiration?.length) {
      return delayed_shipping_expiration;
    }

    return formatDate(getDefaultExpirationDateInUTC().toISOString(), DateFormatsEnum.FullDateAndTime);
  })();

  // Below is a workaround and legacy issue in existing send models
  // Sends have the flag with the name `disable_email_notifications`
  // OneLinks and bulk request have the flag with the name `disable_sender_email_notifications`
  // Both flags are the same, but should be mapped differently in the request
  // isBulk case is not covered here because it's not used there in the current implementation
  const senderNotificationsConfig = {
    [isOneLink ? DISABLE_SENDER_EMAIL_NOTIFICATIONS : DISABLE_EMAIL_NOTIFICATIONS]: disable_sender_email_notifications,
  };

  // let shippingDetails;
  //
  // if (isOneLink) {
  //   const { is_receiver_address_fixed, receiver_fixed_address, org_address_ids } =
  //     engagement as IOneLinkEngagementCandidate;
  //
  //   shippingDetails = {
  //     is_receiver_address_fixed,
  //     org_address_ids,
  //     receiver_fixed_address,
  //   };
  // }

  const hasMarketplaceContext = marketplaceItems !== undefined || giftBudget !== undefined;

  const marketplace = buildMarketplaceRequestMap(marketplaceItems ?? []);

  // The contract wants an explicit `null`, not an empty array, when there are no IE items.
  const requestItems = hasMarketplaceContext && !items?.length ? null : items;

  return {
    ...restEngagement,
    items: requestItems,
    ...(hasMarketplaceContext ? { marketplace, gift_budget: giftBudget ?? null } : {}),
    ...senderNotificationsConfig,
    [SEND_ON_HOLD]: onHoldUntil,
    [DELAYED_SHIPPING_EXPIRATION]: expirationDate,
    ...(!isOneLink ? { [CRM_TYPE]: crmType } : {}),
  };
};
